/**
 * The work on one client: its partner, the obligations it carries, who each is
 * allocated to -- MVP P0-4 / P0-5 (docs/PRACTICE-MVP-PLAN.md).
 *
 * SmartPractice's "Enable client" screen, in the shape the decisions give it:
 *
 *   - only an obligation type a cited deadline rule backs can be added
 *     (§15.2) -- the server lists them, this screen does not decide;
 *   - adding one generates this year's and next year's periods at once and
 *     says which of them could not be dated, and why;
 *   - ending one marks the untouched later periods "not applicable" with the
 *     reason (D7), never deletes them;
 *   - every change here is a manager's (the server enforces it; staff see the
 *     same information without the controls).
 */

import { useCallback, useEffect, useState } from 'react'
import { api, type AuthState, type ClientDetail, type ClientObligation, type StaffMember } from '../api'
import { todayIso } from '../dates'

export function ClientWork({
  client,
  auth,
  dirty,
  onClientChanged,
}: {
  client: ClientDetail
  auth: AuthState
  /** The page has unsaved field edits; changing the partner would discard them. */
  dirty: boolean
  onClientChanged: (c: ClientDetail) => void
}) {
  const isManager = auth.mode === 'anonymous' || auth.user?.role === 'manager'
  const [obligations, setObligations] = useState<ClientObligation[] | null>(null)
  const [staff, setStaff] = useState<StaffMember[]>([])
  const [types, setTypes] = useState<{ code: string; description: string }[]>([])
  const [message, setMessage] = useState<{ tone: 'ok' | 'warn' | 'bad'; text: string } | null>(null)

  const [newType, setNewType] = useState('')
  const [newFrom, setNewFrom] = useState(todayIso())
  const [newWho, setNewWho] = useState('')
  const [ending, setEnding] = useState<{ id: string; date: string } | null>(null)

  const load = useCallback(async () => {
    try {
      const [o, s, t] = await Promise.all([
        api.clientObligations(client.id),
        api.staff(),
        api.assignableObligationTypes(),
      ])
      setObligations(o.obligations)
      setStaff(s.staff)
      setTypes(t.obligationTypes)
    } catch (e) {
      setMessage({ tone: 'bad', text: e instanceof Error ? e.message : String(e) })
    }
  }, [client.id])

  useEffect(() => {
    void load()
  }, [load])

  const run = async (what: () => Promise<string>, tone: 'ok' | 'warn' = 'ok') => {
    try {
      setMessage({ tone, text: await what() })
      await load()
    } catch (e) {
      setMessage({ tone: 'bad', text: e instanceof Error ? e.message : String(e) })
    }
  }

  const openCodes = new Set((obligations ?? []).filter((o) => o.activeTo === null).map((o) => o.obligationTypeCode))
  const addable = types.filter((t) => !openCodes.has(t.code))

  return (
    <section className="rounded-eq-md border border-eq-border bg-white px-5 py-4">
      <h2 className="mb-3 font-heading text-[11px] font-bold uppercase tracking-[.08em] text-eq-neutral">
        Work on this client
      </h2>

      {message && (
        <p
          className={[
            'mb-3 rounded-eq-sm px-3 py-2 text-[12.5px]',
            message.tone === 'ok'
              ? 'bg-eq-success-bg text-eq-success'
              : message.tone === 'warn'
                ? 'bg-eq-warning-bg text-eq-warning'
                : 'bg-eq-danger-bg text-eq-danger',
          ].join(' ')}
        >
          {message.text}
        </p>
      )}

      <label className="mb-4 flex flex-col gap-1 text-[12.5px]">
        <span className="font-semibold text-eq-neutral">Partner</span>
        {isManager ? (
          <select
            className="eq-input-sm max-w-xs"
            value={client.partnerUserId ?? ''}
            disabled={dirty}
            title={dirty ? 'Save or discard the changes above first' : undefined}
            onChange={(event) =>
              void run(async () => {
                const next = event.target.value === '' ? null : event.target.value
                onClientChanged(await api.updateClient(client.id, { partnerUserId: next }))
                return next === null ? 'Partner cleared.' : `Partner set to ${staff.find((p) => p.id === next)?.name ?? 'them'}.`
              })
            }
          >
            <option value="">No partner</option>
            {staff.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        ) : (
          <span>{client.partner ? (client.partner.fullName ?? client.partner.email) : 'No partner'}</span>
        )}
      </label>

      {obligations === null ? (
        <p className="text-[12.5px] text-eq-helper">Loading obligations…</p>
      ) : obligations.length === 0 ? (
        <p className="text-[12.5px] text-eq-helper">This client carries no obligations yet.</p>
      ) : (
        <table className="eq-table text-[13px]">
          <thead>
            <tr>
              <th>Obligation</th>
              <th>From</th>
              <th>Allocated to</th>
              <th>Periods</th>
              {isManager && <th />}
            </tr>
          </thead>
          <tbody>
            {obligations.map((o) => (
              <tr key={o.id} className={o.activeTo !== null ? 'text-eq-helper' : undefined}>
                <td>
                  <span className="font-heading font-bold text-eq-navy">{o.obligationTypeCode}</span>
                  <span className="block text-[12px] text-eq-helper">{o.description}</span>
                  {!o.typeIsActive && (
                    <span className="block text-[11.5px] text-eq-warning">
                      No cited deadline rule yet — periods carry no due date
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap tabular-nums">
                  {o.activeFrom}
                  {o.activeTo !== null && <span className="block">to {o.activeTo}</span>}
                </td>
                <td>
                  {isManager && o.activeTo === null ? (
                    <select
                      className="eq-input-sm"
                      value={o.assignedUserId ?? ''}
                      onChange={(event) =>
                        void run(async () => {
                          const next = event.target.value === '' ? null : event.target.value
                          await api.allocateObligation(o.id, next)
                          return `${o.obligationTypeCode} ${next === null ? 'unallocated' : `allocated to ${staff.find((p) => p.id === next)?.name ?? 'them'}`}.`
                        })
                      }
                    >
                      <option value="">
                        {o.initialsResolveTo
                          ? `${o.initialsResolveTo} (from initials ${o.assignedTo}, not confirmed)`
                          : o.assignedTo
                            ? `Unallocated (imported: ${o.assignedTo}?)`
                            : 'Unallocated'}
                      </option>
                      {staff.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    (o.assignedUserName ??
                      o.initialsResolveTo ??
                      (o.assignedTo ? `${o.assignedTo}? (initials nobody holds)` : 'Unallocated'))
                  )}
                </td>
                <td className="whitespace-nowrap">
                  {o.openPeriodCount} open of {o.periodCount}
                  {o.undatedOpenPeriodCount > 0 && (
                    <span className="block text-[11.5px] text-eq-warning">{o.undatedOpenPeriodCount} without a due date</span>
                  )}
                </td>
                {isManager && (
                  <td className="whitespace-nowrap">
                    {o.activeTo === null &&
                      (ending?.id === o.id ? (
                        <span className="flex items-center gap-1">
                          <input
                            type="date"
                            className="eq-input-sm"
                            value={ending.date}
                            aria-label="Last day the client carries this"
                            onChange={(event) => setEnding({ id: o.id, date: event.target.value })}
                          />
                          <button
                            type="button"
                            className="eq-btn-sm border-[1.5px] border-eq-danger text-eq-danger"
                            onClick={() =>
                              void run(async () => {
                                const r = await api.endObligation(o.id, ending.date)
                                setEnding(null)
                                return (
                                  `${o.obligationTypeCode} ended ${ending.date}. ${r.periodsMarkedNotApplicable} later period(s) marked not applicable` +
                                  (r.periodsLeftForReview > 0 ? `; ${r.periodsLeftForReview} already at for review were left for you to decide.` : '.')
                                )
                              })
                            }
                          >
                            End
                          </button>
                          <button type="button" className="text-[12px] underline" onClick={() => setEnding(null)}>
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="text-[12px] text-eq-danger underline"
                          onClick={() => setEnding({ id: o.id, date: todayIso() })}
                        >
                          End…
                        </button>
                      ))}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isManager && (
        <form
          className="mt-4 flex flex-wrap items-end gap-2 border-t border-eq-border pt-3 text-[12.5px]"
          onSubmit={(event) => {
            event.preventDefault()
            if (newType === '') return
            void run(async () => {
              const r = await api.addObligation(client.id, {
                obligationTypeCode: newType,
                activeFrom: newFrom,
                assignedUserId: newWho === '' ? null : newWho,
              })
              const code = newType
              setNewType('')
              const refused = r.ensure.refusals
              const parts = [`${code} added: ${r.ensure.periodsCreated} period(s) for ${r.ensure.years.join(' and ')}, ${r.ensure.dueDatesSet} dated.`]
              if (refused.length > 0) {
                const reasons = [...new Set(refused.map((x) => x.reason))].slice(0, 2).join(' / ')
                parts.push(`${refused.length} could not be generated or dated: ${reasons}`)
              }
              return parts.join(' ')
            })
          }}
        >
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-eq-neutral">Add obligation</span>
            <select className="eq-input-sm" value={newType} onChange={(event) => setNewType(event.target.value)}>
              <option value="">Choose…</option>
              {addable.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.code} — {t.description}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-eq-neutral">From</span>
            <input type="date" className="eq-input-sm" value={newFrom} onChange={(event) => setNewFrom(event.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-eq-neutral">Allocated to</span>
            <select className="eq-input-sm" value={newWho} onChange={(event) => setNewWho(event.target.value)}>
              <option value="">Unallocated</option>
              {staff.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            disabled={newType === ''}
            className="eq-btn-sm border-[1.5px] border-eq-navy text-eq-navy hover:bg-eq-navy hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            Add
          </button>
          <span className="basis-full text-[12px] text-eq-helper">
            Only obligation types backed by a cited deadline rule can be added. Periods for this
            year and next are created straight away.
          </span>
        </form>
      )}
    </section>
  )
}
